/* 年数月数入力 */
import React, { memo, useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import RowTemplate from '../atoms/RowTemplate';
import Messages from '../molecules/Messages';
import Paragraph from './Paragraph';
import { convertData, numberExtraction } from '../Convert';

interface Props {
  id: string;
  fm_id?: number;
  label: string;
  need?: boolean;
  value: string;
  placeholder: string;
  handleChange: (months: string) => void;
  message?: string;
  name?: string;
  suffix?: string;
  help_msg?: string;
  disabled: boolean;
  maxlength?: number;
  handleBlur?: VoidFunction;
  handleFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  input_assistance?: string;
  looks_inactive?: boolean;
  readonly?: boolean;
}

const YearMonth: React.FC<Props> = ({
  id,
  fm_id,
  label,
  need,
  value,
  placeholder,
  handleChange,
  message,
  name,
  help_msg,
  disabled,
  maxlength,
  handleBlur,
  input_assistance,
  looks_inactive,
  readonly,
}) => {
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [months, setMonths] = useState<Number|null>(null);

  useEffect(() => {
    if (value === null || typeof value === 'undefined' || value === '') {
      setYear('');
      setMonth('');
      return;
    }

    // valueを12で割って年数を割り出す
    setYear(String(Math.floor(Number(value) / 12)));
    // valueを12で割った余りから月数を割り出す
    setMonth(String(Number(value) % 12));
  }, [value, months]);

  // 年数を変更
  const onChangeYear = (event: React.ChangeEvent<HTMLInputElement>) => {
    setYear(event.target.value);
  }

  // 月数を変更
  const onChangeMonth = (event: React.ChangeEvent<HTMLInputElement>) => {
    setMonth(event.target.value);
  };

  // 年、カ月を月数に変換
  const convertMonths = (year?: number|string|null, month?: number|string|null) => {
    // 年、カ月が未入力の場合は月数は空白にする
    if ((year === null || typeof year === 'undefined' || year === '')
      && (month === null || typeof month === 'undefined' || month === ''))
    {
      setMonths(null);
      handleChange('');
      return;
    }

    // 月数変換後を格納する変数の初期化
    let new_months = 0;

    // 年を月数に換算する
    if (year !== null && typeof year !== 'undefined' && year !== '') {
      if (typeof input_assistance !== 'undefined') {
        new_months += numberExtraction(convertData(input_assistance, String(year))) * 12;
      } else {
        new_months += numberExtraction(String(year)) * 12;
      }
    }

    // 月数に入力された月を加算する
    if (month !== null && typeof month !== 'undefined' && month !== '') {
      if (typeof input_assistance !== 'undefined') {
        new_months += numberExtraction(convertData(input_assistance, String(month)));
      } else {
        new_months += numberExtraction(String(month));
      }
    }

    setMonths(Number(new_months));
    handleChange(String(new_months));
  };

  // フォーカスアウト時の処理
  // 年
  const handleBlurYear = () => {
    // 文字列変換処理
    if (typeof input_assistance !== 'undefined') {
      setYear(convertData(input_assistance, year));
    }

    // 月数を計算
    convertMonths(year, month);

    // フォーカスアウト時の処理
    if (typeof handleBlur !== 'undefined') {
      handleBlur();
    }
  };
  // 月
  const handleBlurMonth = () => {
    // 文字列変換処理
    if (typeof input_assistance !== 'undefined') {
      // setMonth(convertData(input_assistance, month));
      if (!convertOverflowMonth(Number(convertData(input_assistance, month)))) {
        setMonth(convertData(input_assistance, month));
      }
    } else {
      if (!convertOverflowMonth(Number(month))) {
        setMonth(month);
      }
    }

    // 月数を計算
    convertMonths(year, month);

    // フォーカスアウト時の処理
    if (typeof handleBlur !== 'undefined') {
      handleBlur();
    }
  };

  /**
   * 月に12以上の値を入力された場合、年、月数の数値変換を行う
   * @param number month 月
   * @return boolean true=数値変換を実施した、false=数値変換を実施しなかった
   */
  const convertOverflowMonth = (month: number) => {
    if (month >= 12) {
      const new_year = (Math.floor(month / 12)) + Number(year);
      const new_month = month % 12;

      setYear(String(new_year));
      setMonth(String(new_month));

      return true;
    }
    return false;
  };

  // プレースホルダー
  const setPlaceholder = () => {
    let ph_year = null;
    let ph_month = null;

    if (placeholder !== null) {
      ph_year = placeholder.match(/^([0-9]+)年/);
      ph_month = placeholder.match(/([0-9]+)ヵ月$/);
    }

    return [ph_year !== null ? ph_year[1] : '', ph_month !== null ? ph_month[1] : ''];
  };

  /**
   * ラベルを描画する
   * @return JSX.Element ラベルのElement
   */
  const returnLabelJsx = () => {
    return <Paragraph label={year !== '' && month !== '' ? `${year}年${month}ヵ月` : ''} />;
  };

  /**
   * 通常の年ヵ月を描画する
   * @return JSX.Element 年ヵ月のElement
   */
  const returnYearMonthJsx = () => {
    return (
      <>
        <Row>
          <Col xs={4} sm={5} md={5} lg={5} xl={5}>
            <input
              id={`${id}-year`}
              type="text"
              className={`form-control ${message && 'textarea-error'}`}
              disabled={disabled || readonly}
              maxLength={maxlength}
              value={year}
              onChange={onChangeYear}
              data-fm_id={fm_id}
              name={`${name}-year`}
              onBlur={handleBlurYear}
              placeholder={setPlaceholder()[0]}
            />
          </Col>
          <div className="d-flex align-items-end justify-content-left">年</div>
          <Col xs={4} sm={5} md={5} lg={5} xl={5}>
            <input
              id={`${id}-months`}
              type="text"
              className={`form-control ${message && 'textarea-error'}`}
              disabled={disabled || readonly}
              maxLength={maxlength}
              value={month}
              onChange={onChangeMonth}
              data-fm_id={fm_id}
              name={`${name}-months`}
              onBlur={handleBlurMonth}
              placeholder={setPlaceholder()[1]}
            />
          </Col>
          <div className="d-flex align-items-end justify-content-left">ヵ月</div>
        </Row>
        <Messages help_msg={help_msg} valid_msg={message} />
      </>
    );
  };

  return (
    <RowTemplate
      label={label}
      need={need}
      disabled={disabled}
      looks_inactive={looks_inactive}
      content={
        <div className="year-month-input">
          {disabled || looks_inactive ? returnLabelJsx() : returnYearMonthJsx()}
        </div>
      }
    />
  );
};

export default memo(YearMonth);
